import React, { useState, useEffect, useRef } from "react";
import { TextField, FormLabel, FormControl } from "@mui/material";
import { parsePhoneNumberFromString } from "libphonenumber-js";

const PhoneNumberField = ({
  label = "Phone Number",
  name = "phoneNumber",
  value,
  onChange,
  error,
  helperText,
  required = true,
  size = "medium",
  disabled = false,
  labelStyle = {},
  FormHelperTextProps,
}) => {
  const [localError, setLocalError] = useState("");
  const inputRef = useRef(null);

  const formatPhoneNumber = (val) => {
    if (!val) return "";
    const digits = val.replace(/\D/g, "");
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const validatePhone = (rawValue) => {
    if (!rawValue) {
      return "";
    }
    const digits = rawValue.replace(/\D/g, "");
    if (digits.length < 10) {
      return `${label} must be 10 digits`;
    }

    try {
      const phoneNumber = parsePhoneNumberFromString(`+1${digits}`);
      if (!phoneNumber || !phoneNumber.isValid() || phoneNumber.country !== "US") {
        return "Invalid number";
      }
    } catch {
      return "Invalid number";
    }

    return "";
  };

const handleChange = (e) => {
  const input = e.target;
  const prevValue = value || ""; 
  const rawPrevDigits = prevValue.replace(/\D/g, "");

  const cursorPos = input.selectionStart;

    // Raw digits only, max 10
  let rawValue = input.value.replace(/\D/g, "").slice(0, 10);

  // Format new value
  const formatted = formatPhoneNumber(rawValue);

  // Figure out cursor shift
  let nextCursor = cursorPos;

  // If user is typing forward (inserting a char)
  if (rawValue.length > rawPrevDigits.length) {
    // If cursor is right after a group that got a dash added
    if (
      (rawValue.length === 4 && cursorPos === 4) || // after first dash
      (rawValue.length === 7 && cursorPos === 8)    // after second dash
    ) {
      nextCursor++;
    }
  }

  // Update parent with raw digits
  onChange({ target: { name, value: rawValue } });

    // After React re-renders, restore cursor position
  requestAnimationFrame(() => {
    if (inputRef.current) {
      inputRef.current.setSelectionRange(nextCursor, nextCursor);
    }
  });
};

  // validate whenever value changes
  useEffect(() => {
    const digits = value ? value.replace(/\D/g, "") : "";
    setLocalError(validatePhone(digits));
  }, [value, label, required]);

  const errorMessage =
    typeof error === "string"
      ? error
      : localError || (typeof helperText === "string" ? helperText : "");

  return (
    <FormControl fullWidth>
      <FormLabel
        sx={{
          color: "black",
          fontWeight: "500",
          fontFamily: "Poppins",
          ...labelStyle,
        }}
      >
        {label} {required && <span style={{ color: "red" }}>*</span>}
      </FormLabel>

      <TextField
        inputRef={inputRef}
        variant="outlined"
        name={name}
        value={formatPhoneNumber(value)}
        onChange={handleChange}
        inputProps={{ maxLength: 12 }}
        disabled={disabled}
        error={Boolean(errorMessage)}
        helperText= {errorMessage}   
        size={size}
        FormHelperTextProps={{
           style: {
      color: "rgb(211, 47, 47)",
      fontFamily: "Roboto, Helvetica, Arial, sans-serif",
      fontWeight: 400,
      fontSize: "0.75rem",
      lineHeight: 1.66,
      letterSpacing: "0.03333em",
      textAlign: "left",
      margin: "3px 14px 0px",
      minHeight: "20px",
    },
          ...FormHelperTextProps,
        }}
        sx={{
          "& .MuiInputBase-root": {
            height: size === "small" ? "40px" : "55px",
            fontSize: size === "small" ? "0.875rem" : "1rem",
          },
        }}
      />
    </FormControl>
  );
};

export default PhoneNumberField;
